<template>
  <div data-testid="financial-form">
    <h2 class="text-h-3">
      {{ getString('register_step3_warren_financial_data_title') }}
    </h2>
    <p class="m-0">
      {{ getString('register_step3_warren_financial_data_description') }}
      <a
        :href="getString('register_step3_warren_financial_data_link_raw')"
        target="_blank"
      >
        (Instrução CVM 506)
      </a>
      .
    </p>
    <hr />
    <InputMoney
      v-model="investmentProfile.monthlyIncome"
      :label="getString('common_field_monthlyincome_label')"
      class="input-money-row"
      data-testid="monthly-income-input"
      allow-zero
      @input="validateField"
    />
    <InputMoney
      v-model="investmentProfile.movableProperty"
      label="Bens móveis"
      class="input-money-row"
      data-testid="movable-property-input"
      required
      allow-zero
      :helper-text="movablePropertyError"
      :status="movablePropertyStatus"
      @input="validateField"
    />
    <InputMoney
      v-model="investmentProfile.realEstate"
      label="Bens imóveis"
      class="input-money-row"
      data-testid="real-estate-input"
      required
      allow-zero
    />
    <InputMoney
      v-model="investmentProfile.totalInvested"
      label="Investimentos"
      class="input-money-row"
      data-testid="total-invested-input"
      allow-zero
      :helper-text="totalInvestedError"
      :status="totalInvestedStatus"
      @input="validateField"
    />
    <InputMoney
      v-model="investmentProfile.privatePension"
      label="Previdência privada"
      class="input-money-row"
      data-testid="private-pension-input"
      required
      allow-zero
    />
    <div class="investment-profile">
      <HeadingDisplay size="small" title="Características" />
      <div class="investment-profile__type">
        <div class="investment-profile__type-label">
          <div class="investment-profile__type-text">
            <BaseText size="p3" :color="NebraskaColors.elementPrimary">
              Pessoa investidora qualificada
            </BaseText>
            <ButtonIconContextual
              icon="EF0080"
              label="Ver mais"
              @click.stop.prevent="isOpen = true"
            />
          </div>
          <ToggleSwitch
            v-model="investmentProfile.isQualifiedInvestor"
            data-testid="qualified-investor-toggle"
            hide-divider
            :disabled="investmentProfile.isProfessionalInvestor"
            @input="validateField"
          />
        </div>
        <BaseText size="p4">
          Ao assinar este termo, afirmo minha condição de investidor qualificado
          e declaro possuir conhecimento sobre o mercado financeiro suficiente
          para que não me sejam aplicáveis um conjunto de proteções legais e
          regulamentares conferidas aos investidores que não sejam qualificados.
          Como investidor qualificado, atesto ser capaz de entender e ponderar
          os riscos financeiros relacionados à aplicação de meus recursos em
          valores mobiliários que só podem ser adquiridos por investidores
          qualificados. Declaro, sob as penas da lei, que possuo investimentos
          financeiros em valor superior a R$ 1.000.000,00 (um milhão de reais).
        </BaseText>
      </div>
      <SendCertificateModal :is-open="isOpen" @close="isOpen = false" />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  InputMoney,
  HeadingDisplay,
  BaseText,
  ToggleSwitch,
  ButtonIconContextual
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { ValidationError } from 'yup';
import SendCertificateModal from './components/SendCertificateModal/SendCertificateModal.vue';
import { getString } from '@/modules/common/helpers/resources';
import { IProfessionalProfile } from '@/types/models/Customer/ProfessionalProfile';
import { schema } from './helpers/validation';

export enum InputStatusType {
  Default = 'default',
  Error = 'error'
}

@Component({
  components: {
    InputMoney,
    HeadingDisplay,
    BaseText,
    ToggleSwitch,
    ButtonIconContextual,
    SendCertificateModal
  }
})
export default class FinancialDataForm extends Vue {
  @Prop({ required: true })
  public investmentProfile!: IProfessionalProfile;

  public getString = getString;

  private movablePropertyError = '';
  private movablePropertyStatus = InputStatusType.Default;

  private totalInvestedError = '';
  private totalInvestedStatus = InputStatusType.Default;

  private isOpen = false;

  readonly NebraskaColors = NebraskaColors;

  public mounted() {
    if (this.investmentProfile.movableProperty === undefined) {
      this.$set(this.investmentProfile, 'movableProperty', 0);
    }
    if (this.investmentProfile.privatePension === undefined) {
      this.$set(this.investmentProfile, 'privatePension', 0);
    }
    if (this.investmentProfile.realEstate === undefined) {
      this.$set(this.investmentProfile, 'realEstate', 0);
    }
    if (this.investmentProfile.totalInvested === undefined) {
      this.$set(this.investmentProfile, 'totalInvested', 0);
    }
    this.validateField();
  }

  private async validateField() {
    try {
      await schema.validate(this.investmentProfile, { abortEarly: false });
      this.movablePropertyError = '';
      this.movablePropertyStatus = InputStatusType.Default;
      this.totalInvestedError = '';
      this.totalInvestedStatus = InputStatusType.Default;
      this.$emit('validate-field', { field: 'financialData', isValid: true });
    } catch (error) {
      if (error instanceof ValidationError) {
        const movablePropertyError = error.inner.find(
          err => err.path === 'movableProperty'
        );
        const totalInvestedError = error.inner.find(
          err => err.path === 'totalInvested'
        );
        if (movablePropertyError) {
          this.movablePropertyError = movablePropertyError.message;
          this.movablePropertyStatus = InputStatusType.Error;
        }
        if (totalInvestedError) {
          this.totalInvestedError = totalInvestedError.message;
          this.totalInvestedStatus = InputStatusType.Error;
        }
      }
      this.$emit('validate-field', { field: 'financialData', isValid: false });
    }
  }
}
</script>

<style lang="less">
.input-money-row {
  margin-bottom: @size-spacing-x500;
}

.investment-profile {
  &__type-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__type-text {
    display: flex;
    align-items: center;
  }
}
</style>
